import React, { useState } from 'react'
import HelpTutorial, { HelpTutorialSlide, useHelpTutorial } from '@fs/zion-help-tutorial'
import { MarginBox, Paragraph, Checkbox, Separator } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { Trans } from 'react-i18next/icu.macro'
import { Link } from '@fs/zion-router'
import { useUser } from '@fs/zion-user'

import slide1Image from './images/slide1.svg'
import slide2Image from './images/slide2.svg'
import slide3Image from './images/slide3.svg'
import slide4Image from './images/slide4.svg'
import slide5Image from './images/slide5.svg'

type EnglandMapsHelpTutorialSlideContentProps = {
  paragraphOneContent: React.ReactNode
  paragraphTwoContent?: React.ReactNode
  checkbox?: React.ReactNode
}

function EnglandMapsHelpTutorialSlideContent({
  paragraphOneContent,
  paragraphTwoContent,
  checkbox = null,
}: Readonly<EnglandMapsHelpTutorialSlideContentProps>): JSX.Element {
  return (
    <MarginBox marginY="sm">
      <Paragraph size="sm">{paragraphOneContent}</Paragraph>
      {paragraphTwoContent && <Paragraph size="sm">{paragraphTwoContent}</Paragraph>}
      <Separator size="nano" />
      {checkbox}
    </MarginBox>
  )
}

export default function EnglandMapsHelpTutorial(): JSX.Element {
  const helpTutorial = useHelpTutorial('england-maps-help-tutorial.overview')
  const [checked, setChecked] = useState(helpTutorial.hideAdChecked)
  const { setShouldShowAd, changeHideAdCheckedTutorialPreference, changeTutorialPreference } = helpTutorial
  const [t] = useTranslation()
  const user = useUser()
  const handleChange = ({ target }: React.ChangeEvent<HTMLInputElement>): void => {
    setChecked(target.checked)
    changeHideAdCheckedTutorialPreference(!target.checked)
    changeTutorialPreference(target.checked)
    setShouldShowAd(false)
  }
  return (
    <HelpTutorial
      tutorialTitle={t('england-maps-help-tutorial.main.heading', 'Learn About Recent Updates')}
      tutorialDescription={t(
        'england-maps-help-tutorial.main.message',
        'Learn how to use the England 1851 Jurisdictions Map to assist with your genealogical research.'
      )}
      endHeader={false}
      {...helpTutorial}
    >
      <HelpTutorialSlide
        imageAlt={t(
          'england-maps-help-tutorial.slide1.heading',
          'Welcome to the updated England 1851 Jurisdictions Map!'
        )}
        imageSrc={slide1Image}
        heading={t(
          'england-maps-help-tutorial.slide1.heading',
          'Welcome to the updated England 1851 Jurisdictions Map!'
        )}
        content={
          <EnglandMapsHelpTutorialSlideContent
            paragraphOneContent={t(
              'england-maps-help-tutorial.slide1.message',
              'The application has been updated. The improved map features clearer outlines of the boundaries, and the application has a new layout for usability purposes. Keep reading to learn about the new map and its features.'
            )}
          />
        }
      />
      <HelpTutorialSlide
        imageAlt={t('england-maps-help-tutorial.slide2.heading', 'Explore the left-hand panel.')}
        imageSrc={slide2Image}
        heading={t('england-maps-help-tutorial.slide2.heading', 'Explore the left-hand panel.')}
        content={
          <EnglandMapsHelpTutorialSlideContent
            paragraphOneContent={t(
              'england-maps-help-tutorial.slide2.message',
              'The collapsible left-hand panel has information for your reference while researching parishes and jurisdictions. It specifically contains three sections: Information, Nearby Parishes, and List. Each section is key for researching the area.'
            )}
          />
        }
      />
      <HelpTutorialSlide
        imageAlt={t('england-maps-help-tutorial.slide3.heading', 'Use the Nearby Parishes section.')}
        imageSrc={slide3Image}
        heading={t('england-maps-help-tutorial.slide3.heading', 'Use the Nearby Parishes section.')}
        content={
          <EnglandMapsHelpTutorialSlideContent
            paragraphOneContent={t(
              'england-maps-help-tutorial.slide3.message',
              'Nearby Parishes is a feature to find parishes within a selected radius. Select the arrow, add a buffer distance, then select Apply. The map will show all parishes surrounding the original search.'
            )}
          />
        }
      />
      <HelpTutorialSlide
        imageAlt={t('england-maps-help-tutorial.slide4.heading', 'Use the Map Layers and Basemap Gallery.')}
        imageSrc={slide4Image}
        heading={t('england-maps-help-tutorial.slide4.heading', 'Use the Map Layers and Basemap Gallery.')}
        content={
          <EnglandMapsHelpTutorialSlideContent
            paragraphOneContent={t(
              'england-maps-help-tutorial.slide4.message',
              'Map Layers give options for displaying boundaries. The Basemap Gallery offers different map views, which are helpful for deciphering the map.'
            )}
          />
        }
      />
      <HelpTutorialSlide
        imageAlt={t('england-maps-help-tutorial.slide-final.heading', 'Share Your Feedback')}
        imageSrc={slide5Image}
        heading={t('england-maps-help-tutorial.slide-final.heading', 'Share Your Feedback')}
        content={
          <EnglandMapsHelpTutorialSlideContent
            paragraphOneContent={
              <Trans
                i18nKey="england-maps-help-tutorial.slide-final.p1.message"
                defaults="We hope you find this tool helpful. For more information about how this resource can help with your research, see the <0>England Jurisdictions 1851 wiki page</0>."
                components={[
                  <Link
                    key="wiki-link"
                    external
                    wayfinding
                    to="https://www.familysearch.org/en/wiki/England_Jurisdictions_1851"
                    target="_blank"
                  />,
                ]}
              />
            }
            paragraphTwoContent={
              <Trans
                i18nKey="england-maps-help-tutorial.slide-final.p2.message"
                defaults="Use the feedback box on the right-hand side of the map to share thoughts about the map or general insights about your experience with the updated tool."
              />
            }
            checkbox={
              user?.signedIn && (
                <Checkbox
                  label={t('help-tutorial.show-again.label', 'Show this help to me again the next time I sign in.')}
                  name="showAgain"
                  checked={checked}
                  onChange={handleChange}
                />
              )
            }
          />
        }
      />
    </HelpTutorial>
  )
}
